// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, DollarSign, Users, CreditCard, Clock } from 'lucide-react';
import { toast } from 'sonner';

interface Event {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
}

interface ReportData {
  totalRegistrations: number;
  confirmedRegistrations: number;
  pendingRegistrations: number;
  totalRevenue: number;
  paidRevenue: number;
  pendingRevenue: number;
}

const StaffViewReports: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [reportData, setReportData] = useState<ReportData>({
    totalRegistrations: 0,
    confirmedRegistrations: 0,
    pendingRegistrations: 0,
    totalRevenue: 0,
    paidRevenue: 0,
    pendingRevenue: 0,
  });

  useEffect(() => {
    const checkPermissionAndLoadData = async () => {
      if (!profile?.email || !eventId) return;

      try {
        // Find staff member by email (consistent with other staff pages)
        const { data: staffRecord, error: staffError } = await supabase
          .from('event_staff')
          .select('id')
          .eq('email', profile.email)
          .eq('is_active', true)
          .maybeSingle();

        if (staffError || !staffRecord) {
          toast.error('You do not have permission to view reports for this event');
          navigate('/staff/dashboard');
          return;
        }

        // Check if staff has view_reports permission for this event
        const { data: assignment } = await supabase
          .from('event_staff_assignments')
          .select('permissions')
          .eq('staff_id', staffRecord.id)
          .eq('event_id', eventId)
          .maybeSingle();

        if (!assignment) {
          toast.error('You do not have permission to view reports for this event');
          navigate('/staff/dashboard');
          return;
        }

        const permissions = assignment.permissions || [];
        if (!permissions.includes('all') && !permissions.includes('view_reports')) {
          toast.error('You do not have permission to view reports for this event');
          navigate('/staff/dashboard');
          return;
        }

        setHasPermission(true);

        // Load event details
        const { data: eventData, error: eventError } = await supabase
          .from('events')
          .select('id, name, start_date, end_date')
          .eq('id', eventId)
          .single();

        if (eventError) throw eventError;
        setEvent(eventData);

        // Load registration data
        const { data: registrations, error: regError } = await supabase
          .from('registrations')
          .select('status, payment_status, registrant_data')
          .eq('event_id', eventId);

        if (regError) throw regError;

        const totalRegistrations = registrations?.length || 0;
        const confirmedRegistrations = registrations?.filter(r => r.status === 'confirmed').length || 0;
        const pendingRegistrations = registrations?.filter(r => r.status === 'pending').length || 0;

        let totalRevenue = 0;
        let paidRevenue = 0;
        let pendingRevenue = 0;

        registrations?.forEach(reg => {
          const total = (reg.registrant_data as any)?.total || 0;
          totalRevenue += total;
          
          if (reg.payment_status === 'paid') {
            paidRevenue += total;
          } else {
            pendingRevenue += total;
          }
        });

        setReportData({
          totalRegistrations,
          confirmedRegistrations,
          pendingRegistrations,
          totalRevenue,
          paidRevenue,
          pendingRevenue,
        });
      } catch (error) {
        console.error('Error loading report data:', error);
        toast.error('Failed to load report data');
      } finally {
        setLoading(false);
      }
    };

    checkPermissionAndLoadData();
  }, [profile?.email, eventId, navigate]);

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  if (!hasPermission || !event) {
    return null;
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/staff/dashboard')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </Button>
      </div>

      <div>
        <h1 className="text-3xl font-bold font-heading">{event.name}</h1>
        <p className="text-muted-foreground">Event reports and analytics</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Registrations</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{reportData.totalRegistrations}</div>
            <p className="text-xs text-muted-foreground">All registration records</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Confirmed</CardTitle>
            <Users className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{reportData.confirmedRegistrations}</div>
            <p className="text-xs text-muted-foreground">Confirmed registrations</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Clock className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{reportData.pendingRegistrations}</div>
            <p className="text-xs text-muted-foreground">Pending registrations</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Revenue Summary</CardTitle>
          <CardDescription>Financial overview for this event</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-3">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-muted-foreground" />
                <p className="text-sm font-medium text-muted-foreground">Total Revenue</p>
              </div>
              <p className="text-3xl font-bold">{formatCurrency(reportData.totalRevenue)}</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-green-600" />
                <p className="text-sm font-medium text-muted-foreground">Paid</p>
              </div>
              <p className="text-3xl font-bold text-green-600">{formatCurrency(reportData.paidRevenue)}</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-orange-600" />
                <p className="text-sm font-medium text-muted-foreground">Pending</p>
              </div>
              <p className="text-3xl font-bold text-orange-600">{formatCurrency(reportData.pendingRevenue)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Navigate to related sections</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Button 
            className="w-full justify-start" 
            variant="outline"
            onClick={() => navigate(`/staff/registrations/${eventId}`)}
          >
            <Users className="mr-2 h-4 w-4" />
            View All Registrations
          </Button>
          <Button 
            className="w-full justify-start" 
            variant="outline"
            onClick={() => navigate(`/staff/manage/${eventId}`)}
          >
            <DollarSign className="mr-2 h-4 w-4" />
            Manage Event
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffViewReports;
